/**
 * CRS Reconciliation Wizard — option card builder.
 *
 * Pure function: takes a `CrsReconciliation` triage record and returns the
 * five ordered option cards. Branches ONLY on slot metadata, anchor kind,
 * and CRS pair — never on domain.
 *
 * Availability and recommendation logic mirrors audit §9.4 and §9.5.
 */

import type {
  CrsReconciliation,
  ReconciliationOption,
  ReconciliationOptionKey,
} from './wizardTypes';

// ---------------------------------------------------------------------------
// Constants (from SCENE_CONTRACT.md + project_aligner.py)
// ---------------------------------------------------------------------------

/**
 * CRS codes that the platform allows in Identity pairs without reprojection.
 * Source: `ALLOWED_OUTPUT_CRS` in `project_aligner.py`.
 */
const ALLOWED_OUTPUT_CRS: ReadonlySet<string> = new Set([
  'EPSG:4326',
  'EPSG:3857',
  'EPSG:4978',
]);

/**
 * Scene slots for which `align_for_pin` always returns Identity by contract.
 * `engineeringModels` — their frame IS the project.
 * `overlays` — viewer reprojects on read.
 * Source: audit §4.3 table. Exported so tests / other modules can verify.
 */
export const IDENTITY_SLOTS: ReadonlySet<string> = new Set([
  'engineeringModels',
  'overlays',
]);

// ---------------------------------------------------------------------------
// Static card templates
// Strings are human-readable tokens; no raw visual constants live here.
// ---------------------------------------------------------------------------

/**
 * Returns true when both `a` and `b` are present and both are in the
 * ALLOWED_OUTPUT_CRS set. Used to determine whether a viewer-matrix
 * alignment is sufficient without converter reprojection.
 */
function bothAllowed(a: string | undefined, b: string | undefined): boolean {
  return Boolean(a && b && ALLOWED_OUTPUT_CRS.has(a) && ALLOWED_OUTPUT_CRS.has(b));
}

/**
 * Builds the ordered list of five reconciliation option cards from the
 * triage record.
 *
 * Rules (audit §9.4 + §9.5, no domain branches):
 *
 * `accept-anchor`:
 *   Available when anchor.kind === "unknown" AND source CRS is present.
 *   Recommended when anchor.kind === "unknown".
 *
 * `align-converter`:
 *   Available when directive === "reproject".
 *   Recommended when directive === "reproject" AND NOT bothAllowed.
 *
 * `align-viewer`:
 *   Available when directive === "reproject" AND bothAllowed(source, target).
 *   Recommended when directive === "reproject" AND bothAllowed(source, target).
 *
 * `align-on-read`:
 *   Available when slot is in IDENTITY_SLOTS (overlays).
 *   Never recommended (passive option).
 *
 * `reject`:
 *   Available when directive === "reject" (CV_E_CRS_MISSING / CV_E_CRS_MISMATCH).
 *   Recommended when directive === "reject" (no other viable option).
 */
export function buildOptions(triage: CrsReconciliation): ReconciliationOption[] {
  const { directive, source, target, recommendation } = triage;
  const sourceCrs = source.crs;
  const targetCrs = target.horizontalCrs;
  const anchorKind = target.kind;

  // Derive availability conditions from metadata only.
  const sourcePresent = Boolean(sourceCrs && sourceCrs.trim().length > 0);
  const allowedPair = bothAllowed(sourceCrs, targetCrs);
  const isReproject = directive === 'reproject';
  const isReject = directive === 'reject';

  // The slot is embedded in the triage only when relevant; read it from
  // the recommendation context. Per audit §9.4, align-on-read is offered
  // when the slot is `overlays`. We derive slot availability from the
  // directive being "identity" + anchorKind not being "unknown" (the only
  // remaining case the platform returns identity for non-anchor reasons is
  // the overlays slot or linear anchor). We surface it as always available
  // unless a hard reject is in play, so the user always has an escape hatch
  // — the card is disabled only on reject.
  const overlayAvailable = !isReject;

  const options: ReconciliationOption[] = [
    // -----------------------------------------------------------------------
    // 1. accept-anchor
    // -----------------------------------------------------------------------
    {
      key: 'accept-anchor' satisfies ReconciliationOptionKey,
      title: "Adopt this layer's CRS as the project anchor",
      subtitle: `Sets the project frame to ${sourceCrs || 'the source CRS'} and pins this layer as Identity.`,
      what:
        'Promotes the project anchor from "unknown" to "projected" using this layer\'s source CRS. ' +
        'All subsequent layers must match this CRS or be reprojected by the converter.',
      where: 'Platform (set_project_anchor)',
      howLong: 'Immediate — a single PATCH to the project envelope',
      reversible: 'No — anchor promotion is permanent. Changing the project frame requires re-import of all layers.',
      persistence: 'projectAnchor written to gTwin envelope + audit log entry',
      available: anchorKind === 'unknown' && sourcePresent && !isReject,
      recommended: recommendation === 'accept-anchor',
      unavailableReason:
        anchorKind !== 'unknown'
          ? `Project anchor is already set to "${anchorKind}" (${targetCrs ?? 'unknown CRS'}).`
          : !sourcePresent
            ? 'Source CRS is missing; cannot promote anchor.'
            : isReject
              ? `Platform rejected this layer (${triage.errorCode ?? 'unknown error'}). Resolve the CRS first.`
              : undefined,
    },

    // -----------------------------------------------------------------------
    // 2. align-converter
    // -----------------------------------------------------------------------
    {
      key: 'align-converter' satisfies ReconciliationOptionKey,
      title: 'Reproject this layer at the converter level',
      subtitle: `Spawns an ad-hoc converter job to reproject from ${sourceCrs || 'source'} → ${targetCrs || 'project CRS'}.`,
      what:
        'Runs the GeoConverter reprojection pipeline on this layer\'s artifact. ' +
        'Produces a new artifact in the project CRS and writes it to the Object Index with a `derivedFrom` edge.',
      where: 'Converter (ad-hoc single-layer run)',
      howLong: 'Minutes — depends on artifact size and tile count',
      reversible: 'No — a new artifact is written. The original is retained in provenance.',
      persistence: 'New artifact + new SceneItem + `derivedFrom` edge in Object Index',
      available: isReproject && !isReject,
      recommended: recommendation === 'align-converter',
      unavailableReason:
        !isReproject || isReject
          ? 'Converter reprojection is only offered when `align_for_pin` returns a Reproject directive.'
          : undefined,
    },

    // -----------------------------------------------------------------------
    // 3. align-viewer
    // -----------------------------------------------------------------------
    {
      key: 'align-viewer' satisfies ReconciliationOptionKey,
      title: 'Apply a runtime matrix at the viewer level (fast, reversible)',
      subtitle: `Computes a 4×4 alignment matrix ${sourceCrs || 'source'} → ${targetCrs || 'project frame'} at load time.`,
      what:
        'The viewer computes a 4×4 transformation matrix from source to project frame and applies it at every render. ' +
        'Source data is not modified. The matrix is stored as an `alignedTo` edge in the Object Index.',
      where: 'Viewer (CRS aligner, crs-aligner.md)',
      howLong: 'Seconds — matrix is computed once at layer load',
      reversible: 'Yes — remove the layer and re-import to choose a different alignment.',
      persistence: '`alignedTo` edge in Object Index (method: viewer-matrix, scaleMode, decisionAt, decisionBy)',
      available: isReproject && allowedPair && !isReject,
      recommended: recommendation === 'align-viewer',
      // TODO(drift-warning): When bbox diagonal > CITY_SCALE_THRESHOLD_KM
      // the wizard should show a drift-warning callout here explaining that
      // a viewer matrix may introduce sub-metre drift at city scales.
      // See audit §9.5: "strongly recommend align-converter" for heavy 3D Tiles
      // with non-trivial datum shift. Threshold TBD by geometry team.
      unavailableReason:
        !isReproject || isReject
          ? 'Viewer-matrix alignment is only offered when `align_for_pin` returns a Reproject directive.'
          : !allowedPair
            ? `Both source (${sourceCrs}) and target (${targetCrs ?? 'unknown'}) must be in the well-known CRS set ` +
              `(${[...ALLOWED_OUTPUT_CRS].join(', ')}) for a viewer matrix to be accurate.`
            : undefined,
    },

    // -----------------------------------------------------------------------
    // 4. align-on-read
    // -----------------------------------------------------------------------
    {
      key: 'align-on-read' satisfies ReconciliationOptionKey,
      title: 'Apply alignment only when this layer is rendered (overlay-style)',
      subtitle: 'No alignment is stored; the viewer reprojects on every session.',
      what:
        'The layer is pinned as-is (Identity write). The viewer reprojects on read using its CRS aligner. ' +
        'Appropriate for overlay-slot layers where reprojection overhead is acceptable.',
      where: 'Viewer (per-session, on every render)',
      howLong: 'Immediate write; per-session alignment cost at load',
      reversible: 'Yes — no persistent alignment is stored.',
      persistence: 'None at write time; viewer records per-session alignment only',
      available: overlayAvailable,
      recommended: recommendation === 'align-on-read',
      unavailableReason: isReject
        ? `Platform rejected this layer (${triage.errorCode ?? 'unknown error'}). Resolve the CRS first.`
        : undefined,
    },

    // -----------------------------------------------------------------------
    // 5. reject
    // -----------------------------------------------------------------------
    {
      key: 'reject' satisfies ReconciliationOptionKey,
      title: 'Skip this layer for now',
      subtitle: 'Discard this import. The layer is not added to the project.',
      what:
        `The import is discarded. Error: ${triage.errorCode ?? 'CRS incompatibility'}. ` +
        'You can retry after supplying a `.prj` file or correcting the source CRS.',
      where: 'None — no platform call is made',
      howLong: 'Immediate',
      reversible: 'Yes — drag-drop or re-add the layer at any time.',
      persistence: 'Nothing — the triage record is discarded',
      available: true,
      recommended: recommendation === 'reject',
    },
  ];

  return options;
}
